import { isAxiosError } from "axios";
import { useFormikContext } from "formik";
import { useRef, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import {
  UploadFileCard,
  type UploadStatus
} from "@/components/upload/UploadFileCard";
import { UploadSurface } from "@/components/upload/UploadSurface";
import { MetaLine } from "@/components/ui/meta-line";

import type { CollectedFile } from "../../caseFiles/upload/collectDroppedFiles";
import { KeyFactsAPI } from "../../keyFacts/api";
import type { NoticePreview } from "../../keyFacts/types";
import type { NewCaseValues } from "../types";
import { noticeSummary } from "../utils";

const ACCEPTED = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".tif",
  ".tiff",
  ".docx"
] as const;
/** Each attempt waits up to 45s server-side, so about three minutes in all. */
const PENDING_ATTEMPTS = 4;

const isAccepted = (name: string) => {
  const lower = name.toLowerCase();
  return ACCEPTED.some((ext) => lower.endsWith(ext));
};

/**
 * The notice the case answers, uploaded first. It is read right away and
 * fills in the base filing, response due date and petitioner; the file itself
 * is added to the case when it is created, where its receipt is tracked.
 */
export const NoticeStep: FC = () => {
  const { values, setFieldValue } = useFormikContext<NewCaseValues>();
  const current = useRef<File | null>(null);
  const preview = values.noticePreview;

  const read = async (file: File, attempt: number): Promise<void> => {
    let out: NoticePreview;
    try {
      out = await KeyFactsAPI.previewNotice(file);
    } catch (error) {
      const timedOut = isAxiosError(error) && error.code === "ECONNABORTED";
      out = { status: timedOut ? "pending" : "failed", checksum: "" };
    }
    if (current.current !== file) return;
    // A pending read is still running server-side; posting the same bytes again
    // waits on that run (or takes its cached result) rather than starting another.
    if (out.status === "pending" && attempt < PENDING_ATTEMPTS) {
      await read(file, attempt + 1);
      return;
    }
    void setFieldValue("noticePreview", out, false);
  };

  const pick = async (file: File) => {
    current.current = file;
    void setFieldValue("noticeFile", file, false);
    void setFieldValue("noticePreview", { status: "reading" }, false);
    await read(file, 1);
  };

  const onFiles = (collected: CollectedFile[]) => {
    if (!collected.length) return;
    const file = collected.find((c) => isAccepted(c.file.name))?.file;
    if (!file) {
      toast.error("Upload a PDF, Word file or image of the notice.");
      return;
    }
    void pick(file);
  };

  const clear = () => {
    current.current = null;
    void setFieldValue("noticeFile", null, false);
    void setFieldValue("noticePreview", null, false);
  };

  const card = ((): {
    status: UploadStatus;
    subtitle: ReactNode;
    tone?: "muted" | "warn";
    details?: ReactNode;
  } => {
    switch (preview?.status) {
      case "ready":
        return {
          status: "ready",
          subtitle: "Read",
          details: preview.prefill ? (
            <div
              className="mt-2 flex flex-col gap-1 pl-16"
              data-testid="notice-summary"
            >
              <MetaLine
                parts={noticeSummary(preview.prefill)}
                className="flex-wrap"
              />
              {preview.prefill.issue_titles.length ? (
                <ol className="list-decimal pl-4 text-sm text-fg-2">
                  {preview.prefill.issue_titles.map((title) => (
                    <li key={title}>{title}</li>
                  ))}
                </ol>
              ) : null}
            </div>
          ) : null
        };
      case "pending":
        return {
          status: "info",
          tone: "warn",
          subtitle:
            "Still reading. It finishes in Case Insights once the case opens."
        };
      case "failed":
        return {
          status: "muted",
          subtitle:
            "Couldn't read it here. It is still added to the case and read there."
        };
      default:
        return { status: "busy", subtitle: "Reading the notice…" };
    }
  })();

  return (
    <div className="flex max-w-160 flex-col gap-3">
      {values.noticeFile ? (
        <ul>
          <UploadFileCard
            id="notice"
            name={values.noticeFile.name}
            state={preview?.status ?? "reading"}
            status={card.status}
            subtitle={card.subtitle}
            subtitleTone={card.tone}
            onRemove={clear}
            testId="notice-card"
          >
            {card.details}
          </UploadFileCard>
        </ul>
      ) : (
        <UploadSurface
          onFiles={onFiles}
          multiple={false}
          folders={false}
          accept={ACCEPTED}
          hint="PDF, DOCX, PNG, JPG or TIFF"
        />
      )}
      <p className="text-sm text-fg-3">
        Optional. CaseBlink reads the receipt, response due date and every issue
        USCIS raised, and fills in the rest of this form.
      </p>
    </div>
  );
};
